import {
    Control,
    FieldErrors,
    FieldValues,
    SubmitHandler,
    UseFormGetValues,
    UseFormHandleSubmit,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch
} from 'react-hook-form'
import { CustomDatePicker } from 'components/Datepicker'
import { InputLabel } from 'components/InputLabel'
import { TextArea } from 'components/TextArea'
import { InputSelect } from 'components/InputSelect'
import { Label } from 'components/InputLabel/styles'
import { Toggle } from 'components/Toggle'
import { VariableReducer } from 'types/variable'
import VariableActions from 'actions/Variable'
import { CustomToggle } from 'components/ToggleYesOrNot/CustomToggle'
import { BlueButton } from 'pages/NotifyEventPatient/styles'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useState
} from 'react'
import { TitleNotifications } from 'pages/Notifications/Header/styles'
import YellowCircleInterrogation from 'components/Icons/YellowCircleInterrogation'
import { NotifyEventPatientType } from 'validator/notify_event_patient_validator'
import { LocationReducer } from 'types/location'
import LocationActions from 'actions/Location'
import { SectorReducer } from 'types/sector'
import SectorActions from 'actions/Sector'
import NotificationService from 'services/Notification'
import { useNavigate } from 'react-router-dom'
import { ROUTES_NAMES } from 'constants/name.routes'
import { ICreateNewNotificationResponse } from 'types/notification'
import { inputRequiredStyle } from 'components/Datepicker/styles'
import GoUpComponent from 'components/ScrollUp'
import { useIsMobileScreen } from 'hooks/useMediaQuery'
import FormModals from './FormModals'

interface NotifyEventFormProps {
    trigger: any
    isDirty: boolean
    errors: FieldErrors<FieldValues>
    control: Control<FieldValues, any>
    watch: UseFormWatch<NotifyEventPatientType>
    register: UseFormRegister<FieldValues>
    setValue: UseFormSetValue<NotifyEventPatientType>
    getValues: UseFormGetValues<NotifyEventPatientType>
    handleSubmit: UseFormHandleSubmit<NotifyEventPatientType, undefined>
    setResponseData: Dispatch<SetStateAction<ICreateNewNotificationResponse>>
    reset: any
}
export default function NotifyEventForm({
    register,
    control,
    errors,
    handleSubmit,
    watch,
    setValue,
    getValues,
    isDirty,
    trigger,
    setResponseData,
    reset,
}: NotifyEventFormProps) {
    const isMobile = useIsMobileScreen()

    const NOTIFICATION_VERSION = process.env.REACT_APP_NOTIFICATION_VERSION || 'v4'

    const [showDamageLevelInfoModal, setShowDamageLevelInfoModal] = useState(false)
    const [showNotificationTypeInfoModal, setShowNotificationTypeInfoModal] = useState(false)
    const [showReviewBeforeSubmit, setShowReviewBeforeSubmit] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [showErrorMsg, setShowErrorMsg] = useState<boolean>(false)
    const [uploadS3ErrorMsg, setuploadS3ErrorMsg] = useState<boolean>(false)
    const [attachmentsURL, setAttachmentsURL] = useState<string>()

    //hooks
    const navigate = useNavigate()
    const watchFormData = watch()
    const dispatch = useDispatch()

    //redux state
    const variableSelector: VariableReducer = useSelector(
        (state: RootReducerState) => state.variableReducers
    )
    const locationSelector: LocationReducer = useSelector(
        (state: RootReducerState) => state.locationReducers
    )
    const sectorSelector: SectorReducer = useSelector(
        (state: RootReducerState) => state.sectorReducers
    )

    useEffect(() => {
        VariableActions.getAllVariables(dispatch)
        LocationActions.getAllLocations(dispatch, true)
    }, [])

    const onChangeLocation = useCallback((value) => {
        setValue('location_incident', value, { shouldDirty: true })
        setValue('incident_sector', '')
        SectorActions.getAllSectorsByLocationId(dispatch, value, "", "true")
    }, [])

    const submitForum: SubmitHandler<NotifyEventPatientType> = async (data) => {
        data.notification_type = data.notification_type_toggle != null && (data.notification_type_toggle == true) ? 'identified' : 'anonymous'

        // Campo de anexo comentado - lógica desabilitada temporariamente
        // if (attachmentsURL) {
        //     data.u_attachments_url = attachmentsURL
        //     data.u_attachments_string = selectedFile.name
        //     data.u_attachments_size = selectedFile.size.toString()
        // }

        // data.u_version = NOTIFICATION_VERSION

        const object = {
            sysparm_quantity: 1,
            variables: data
        }

        try {
            const response = await NotificationService.createNotificationByPatient(object)
            
            if (!response || !response.sys_id) {
                console.error('Erro ao criar notificação: resposta inválida', response)
                alert('Erro ao criar notificação. Por favor, tente novamente.')
                return
            }

            // Campo de anexo comentado - lógica de atualização desabilitada temporariamente
            // const updateNotification = {
            //     u_attachments_url: object.variables.u_attachments_url,
            //     u_attachments_string: object.variables.u_attachments_string,
            //     u_attachments_size: object.variables.u_attachments_size,
            //     u_version: NOTIFICATION_VERSION
            // }

            // await NotificationService.updateNotificationGeneric(updateNotification, response.sys_id)

            setResponseData(response)

            navigate(ROUTES_NAMES.notifySentPatient, { replace: true })
        } catch (error) {
            console.error('Erro ao criar notificação:', error)
            alert('Erro ao criar notificação. Por favor, tente novamente.')
        }
    }

    const handleGoToContinue = async () => {
        setShowErrorMsg(false)
        const isValid = await trigger()
        if (isValid) {
            window.scrollTo(0, 0)
            setShowReviewBeforeSubmit(true)
        }
    }

    useEffect(() => {
        if (showErrorMsg && uploadS3ErrorMsg) {
            setSelectedFile(null)
        }
    }, [showErrorMsg, uploadS3ErrorMsg]);

    const chooseDamageLevel = (id: string) => {
        setValue('damage_level', id, { shouldDirty: true })
        setShowDamageLevelInfoModal(false)
    }

    const [progressValue, setProgressValue] = useState<number>(0)
    const [isDisabled, setIsDisabled] = useState<boolean>()

    useEffect(() => {
        if (selectedFile) {
            setIsDisabled(true)

            const uploadFile = async () => {
                try {
                    const { url } = await NotificationService.createPresignedURL(selectedFile, setProgressValue);

                    if (url) {
                        console.log("Anexo enviado com sucesso!");
                        setAttachmentsURL(url)
                        setShowErrorMsg(false)
                    }

                } catch (error) {
                    setShowErrorMsg(true);
                    setuploadS3ErrorMsg(true);
                    console.error("Erro ao enviar o anexo", error);
                } finally {
                    setIsDisabled(false)
                }
            };

            uploadFile();
        }

    }, [selectedFile]);

    useEffect(() => {
        if (attachmentsURL && selectedFile === null) {
            setProgressValue(0)

            const dropFile = async () => {
                try {
                    await NotificationService.deleteS3AttachedFile(attachmentsURL);
                    console.log("Anexo removido com sucesso!");
                    setProgressValue(0)
                } catch (error) {
                    console.error("Erro ao remover o anexo", error);
                } finally {
                    setIsDisabled(false)
                }
            };

            dropFile();
        }

    }, [selectedFile, attachmentsURL]);

    return (
        <div data-testid="notify-event-patient-form">
            <FormModals
                isDirty={isDirty}
                isMobile={isMobile}
                getValues={getValues}
                submitForum={submitForum}
                handleSubmit={handleSubmit}
                watchFormData={watchFormData}
                chooseDamageLevel={chooseDamageLevel}
                showReviewBeforeSubmit={showReviewBeforeSubmit}
                showDamageLevelInfoModal={showDamageLevelInfoModal}
                setShowReviewBeforeSubmit={setShowReviewBeforeSubmit}
                setShowDamageLevelInfoModal={setShowDamageLevelInfoModal}
                showNotificationTypeInfoModal={showNotificationTypeInfoModal}
                setShowNotificationTypeInfoModal={setShowNotificationTypeInfoModal}
                thereIsFile={selectedFile != null}
            />
            <div className="pt-[18px]  md:px-8 px-6 relative">
                <TitleNotifications>Nova notificação</TitleNotifications>
                <p className="text-darkGray text-sm mt-1">
                    Preencha os campos abaixo com os dados do incidente
                </p>
            </div>
            <hr className={'border-[1px]  border-[#EBEDF2] mt-4'} />
            <div className="md:pt-[29px] mt-6 md:px-8 px-6 pb-6">
                <div className="flex gap-4 md:flex-row flex-col">
                    <div className="850px:w-1/5 md:w-2/5 w-1/2  z-10">
                        <CustomDatePicker
                            name="date_incident"
                            control={control}
                            required
                            label="Data da ocorrência"
                            placeholderData="Selecionar"
                            maxDate={new Date()}
                            isError={Boolean(errors.date_incident)}
                            error={Boolean(errors.date_incident)}
                            customStyle="text-sm"
                        />
                    </div>
                    <div className="850px:w-4/5 md:w-3/5 w-full">
                        <InputLabel
                            label="Título da notificação"
                            name="incident_title"
                            register={register}
                            className={``}
                            placeholder="Digite aqui"
                            maxLength={80}
                            required
                            errorStyle="{ w-[741px] }"
                            error={{
                                message: errors.incident_title
                                    ? 'Preenchimento obrigatório.'
                                    : ''
                            }}
                        />
                    </div>
                </div>
                <div className="flex md:gap-4 gap-6 md:flex-row flex-col mt-6">
                    <div className="flex-1">
                        <TextArea
                            className="min-h-[257px]"
                            label="Descrição do incidente"
                            subLabel="Por favor, descreva o que aconteceu com o maior número de detalhes possíveis."
                            name="incident_description"
                            register={register}
                            placeholder="Digite aqui"
                            maxLength={2000}
                            required
                            rows={10}
                            inputLength={
                                2000 -
                                watchFormData.incident_description?.length
                            }
                            error={{
                                message: errors.incident_description
                                    ? 'Preenchimento obrigatório.'
                                    : ''
                            }}
                        />
                    </div>
                </div>

                {/* Campos comentados temporariamente - removidos do formulário do paciente */}
                {/* <div className="flex md:gap-4 gap-6 md:flex-row flex-col md:mt-6 mt-10 ">
                    <div className="850px:w-4/5 md:w-3/5 w-full">
                        <InputSelect
                            placeholder="Selecionar"
                            required={true}
                            styleBoxOptions="mt-3.5"
                            label="Qual o Tipo do incidente"
                            name="incident_type"
                            control={control}
                            shouldHaveSearch={true}
                            searchPlaceHolder="Buscar"
                            data={variableSelector.variablesListIncidentType
                                ?.slice(1)
                                .map((l) => ({
                                    value: l?.value,
                                    label: l?.label
                                }))
                                .sort((o1, o2) =>
                                    o1.label?.localeCompare(o2.label)
                                )}
                            defaultValue={''}
                            errorStyle={`${errors.incident_type ? inputRequiredStyle : ''
                                }`}
                        />
                    </div>
                </div>
                <div className="flex mt-6 md:mr-4">
                    <div className="md:w-1/2 w-full relative">
                        <div className=" flex items-center text-center">
                            <Label className="mr-2">Classificação do Incidente</Label>
                            <div className="flex items-center cursor-pointer" onClick={() => { setShowDamageLevelInfoModal(true) }}>
                                <YellowCircleInterrogation />
                                <span className="ml-2 text-sm underline font-semibold text-blueTheme">
                                    Saiba mais
                                </span>
                            </div>

                        </div>
                        <InputSelect
                            placeholder="Selecionar"
                            required={true}
                            styleBoxOptions="mt-3.5"
                            name="damage_level"
                            control={control}
                            shouldHaveSearch={true}
                            searchPlaceHolder="Buscar"
                            data={variableSelector.variablesListDamageLevel
                                ?.slice(1)
                                .map((l) => ({
                                    value: l?.value,
                                    label: l?.label
                                }))}
                            defaultValue={''}
                            errorStyle={`${errors.damage_level ? inputRequiredStyle : ''
                                }`}
                        />
                    </div>
                </div> */}
                <hr className={'border-[1px]  border-[#EBEDF2] mt-6'} />
                <div className="flex gap-4 md:flex-row flex-col mt-6">
                    <div className="850px:w-4/5 md:w-3/5 w-full">
                        <InputSelect
                            placeholder="Selecionar"
                            required={true}
                            styleBoxOptions="mt-3.5"
                            onChange={onChangeLocation}
                            label="Hospital onde o evento ocorreu"
                            name="location_incident"
                            control={control}
                            shouldHaveSearch={true}
                            searchPlaceHolder="Buscar"
                            data={locationSelector?.locationsList
                                ?.map((l) => ({
                                    value: l?.sys_id,
                                    label: l?.name
                                }))
                                ?.sort((o1, o2) =>
                                    o1.label?.localeCompare(o2.label)
                                )}
                            defaultValue={''}
                            errorStyle={`${errors.location_incident
                                ? inputRequiredStyle
                                : ''
                                }`}
                        />
                    </div>
                     <div className="850px:w-4/5 md:w-3/5 w-full">
                        <InputSelect
                            placeholder="Selecionar"
                            required={true}
                            styleBoxOptions="mt-3.5"
                            label="Setor onde o evento ocorreu (setor notificado)"
                            name="incident_sector"
                            control={control}
                            shouldHaveSearch={true}
                            searchPlaceHolder="Buscar"
                            data={sectorSelector.sectorsList
                                ?.map((l) => ({
                                    value: l?.sys_id,
                                    label: l?.u_name
                                }))
                                ?.sort((o1, o2) =>
                                    o1.label?.localeCompare(o2.label)
                                )}
                            defaultValue={''}
                            errorStyle={`${errors.incident_sector ? inputRequiredStyle : ''
                                }`}
                        />
                    </div>
                </div>
                <div className="w-full mt-4">
                    <Label>Turno que o evento ocorreu</Label>
                    <Toggle
                        isError={!!errors?.shift_incident}
                        required={true}
                        register={register}
                        name="shift_incident"
                        itens={variableSelector.variablesListNotificationTurn
                            ?.slice(1)
                            ?.map((t) => ({
                                id: t.index,
                                title: t.label,
                                value: t.value
                            }))}
                        defaultCheckedValue="NI"
                    />
                    <span className="text-errorText text-xs">
                        {errors.shift_incident
                            ? 'Preenchimento obrigatório.'
                            : ''}
                    </span>
                </div>

                <hr className={'border-[1px]  border-[#EBEDF2] mt-6'} />
                <div className="flex gap-4 md:flex-row flex-col mt-6">
                    <div className="md:w-1/2 w-full">
                        <div className="flex items-center">
                            <p className="line-clamp-1 font-semibold text-xs text-darkGray break-words mr-2" style={isMobile ? { maxWidth: '200px' } : {}}>
                                A sua notificação pode ser anônima ou identificada.
                            </p>
                            <div className="flex items-center cursor-pointer" onClick={() => { setShowNotificationTypeInfoModal(true) }}>
                                <YellowCircleInterrogation />
                                <span className="ml-2 text-sm underline font-semibold text-blueTheme">
                                    Saiba mais
                                </span>
                            </div>
                        </div>
                        <CustomToggle
                            control={control}
                            name={'notification_type_toggle'}
                            enabled={watchFormData.notification_type_toggle}
                            setEnabled={(b) => {
                                setValue('notification_type_toggle', b, { shouldDirty: true })
                                setValue('reporter_name', '')
                            }}
                            leftText="Anônima"
                            rightText="Quero me identificar"
                        />
                    </div>
                </div>
                {watchFormData.notification_type_toggle ? (
                    <div className="mt-6">
                        <InputLabel
                            label="Seu nome completo"
                            name="reporter_name"
                            register={register}
                            className={``}
                            placeholder="Digite aqui"
                            maxLength={80}
                            required
                            errorStyle="{ w-[741px] }"
                            error={{
                                message: errors.reporter_name
                                    ? 'Preenchimento obrigatório. Caso não queira se identificar, volte e selecione a opção "Notificação anônima.'
                                    : ''
                            }}
                        />
                    </div>
                ) : null}

                {/* Campo comentado temporariamente - removido do formulário do paciente */}
                {/* <Label className='mt-4 mb-2'>
                    Anexar Documento (opcional)
                </Label>
                <DropFile
                    onFileSelect={(file: File) => setSelectedFile(file)}
                    errorFlag={showErrorMsg}
                    setErrorFlag={setShowErrorMsg}
                    isMobile={isMobile}
                    progressValue={progressValue}
                    setProgressValue={setProgressValue}
                /> */}

                <div className="mt-11 flex gap-4 items-center">
                    <BlueButton
                        type="button"
                        className={`w-[363px] ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={handleGoToContinue}
                        disabled={isDisabled}
                    >
                        Continuar
                    </BlueButton>
                </div>
            </div>
            <GoUpComponent />
        </div>
    )
}

